import { FC, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Divider from "@mui/material/Divider";
import { Button, IconButton, InputAdornment } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { useLanguageTranslator } from "../../components";
import { EyeCloseIcon, EyeOpenIcon } from "../../@lib/icons";
import { ToastContainer } from "react-toastify";
import { toastHandler } from "../../utils/helpers";
import { toastTypesKeys } from "../../utils/constants";

// mutation {
//   updatePassword(
//     input: {
//       currentPassword: "string"
//       newPassword: "string"
//       newPasswordConfirmation: "string"
//     }
//   ) {
//     clientMutationId
//     user {
//       companyName
//       email
//       firstName
//       id
//       isConfirmed
//       isLocked
//       lang
//       lastName
//       profile{name}
//     }
//   }
// }

const updatePasswordMutationQuery = gql`
  mutation UpdatePassword($input: UpdatePasswordInput!) {
    updatePassword(input: $input) {
      clientMutationId
      user {
        companyName
        email
        firstName
        id
        isConfirmed
        isLocked
        lang
        lastName
        profile {
          name
        }
      }
    }
  }
`;

const PasswordUpdateForm = ({ user }: any) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [isValid, setIsValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState(false);

  const { translator } = useLanguageTranslator();

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleNewPasswordVisibility = () => {
    setNewPassword((prev) => !prev);
  };

  const toggleConfirmNewPasswordVisibility = () => {
    setConfirmNewPassword((prev) => !prev);
  };

  const [updatePassword, { data, loading, error }] = useMutation(
    updatePasswordMutationQuery,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  function handleChange(e: any) {
    const { name, value } = e?.target;
    if (name === "newPassword") {
      setFormData({ ...formData, [name]: value });
      setIsValid(value.length > 8);
      return;
    }

    setFormData({ ...formData, [name]: value });
  }

  async function handleSubmit() {
    try {
      if (formData?.newPassword === formData?.confirmNewPassword) {
        const response = await updatePassword({
          variables: {
            input: {
              currentPassword: formData?.currentPassword,
              newPassword: formData?.newPassword,
              newPasswordConfirmation: formData?.confirmNewPassword,
            },
          },
        });
        if (response?.data?.updatePassword?.user?.id) {
          toastHandler(`Password Updated Successfully`, toastTypesKeys.success);
        }
      } else {
        toastHandler(
          "New password and confirm new password are not same",
          toastTypesKeys.error
        );
      }
    } catch (err) {
      const errorCatch = JSON.stringify(err);
      const parsedError = JSON.parse(errorCatch);

      toastHandler(parsedError?.message || "", toastTypesKeys.error);
    }
  }

  const isFormFieldsFill =
    !formData?.currentPassword ||
    !formData?.newPassword ||
    !formData?.confirmNewPassword;

  return (
    <>
      <ToastContainer />
      <form>
        <Box py={4}>
          <Typography fontSize={20} fontWeight={500}>
            {translator("generic.Password")}
          </Typography>
          <Typography fontWeight={300}>
            {translator(
              "Please enter your current password to change your password"
            )}
            .
          </Typography>
        </Box>

        <Stack gap={3}>
          <Divider />

          <Stack direction={{ md: "row" }} gap={1}>
            <Typography sx={{ flexBasis: { md: "40%", lg: "30%", xl: "25%" } }}>
              {translator("Current password")}
            </Typography>
            <FormControl sx={{ flexBasis: { md: "60%", lg: "40%" } }}>
              <OutlinedInput
                name="currentPassword"
                type={showPassword ? `text` : "password"}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={togglePasswordVisibility} edge="end">
                      {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Stack>

          <Divider />

          <Stack direction={{ md: "row" }} gap={1}>
            <Typography sx={{ flexBasis: { md: "40%", lg: "30%", xl: "25%" } }}>
              {translator("New password")}
            </Typography>
            <FormControl sx={{ flexBasis: { md: "60%", lg: "40%" } }}>
              <OutlinedInput
                name="newPassword"
                type={newPassword ? `text` : "password"}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleNewPasswordVisibility}
                      edge="end"
                    >
                      {newPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText sx={{ mt: 1, color: isValid ? "black" : "red" }}>
                {translator("Your new password must be more than 8 characters")}
                .
              </FormHelperText>
            </FormControl>
          </Stack>

          <Divider />

          <Stack direction={{ md: "row" }} gap={1}>
            <Typography sx={{ flexBasis: { md: "40%", lg: "30%", xl: "25%" } }}>
              {translator("Confirm new password")}
            </Typography>
            <FormControl sx={{ flexBasis: { md: "60%", lg: "40%" } }}>
              <OutlinedInput
                name="confirmNewPassword"
                type={confirmNewPassword ? `text` : "password"}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleConfirmNewPasswordVisibility}
                      edge="end"
                    >
                      {confirmNewPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Stack>
        </Stack>
      </form>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <Button size="large" variant="contained" color="inherit" sx={{ px: 2 }}>
          {translator("Cancel")}
        </Button>
        <Button
          size="large"
          variant="contained"
          sx={{
            px: 2,
            opacity: isFormFieldsFill ? 0.7 : 1,
            cursor: isFormFieldsFill ? "default" : "pointer",
          }}
          onClick={isFormFieldsFill ? () => {} : handleSubmit}
        >
          {translator("Update password")}
        </Button>
      </Stack>
    </>
  );
};

export default PasswordUpdateForm;
